/**
 * Archive Event: package the whole event into a single ZIP for offline storage.
 *
 * Output format (Opção A — sem JSON):
 *   • xlsx  — listas tabulares (vendas, compradores, cupões, check-ins, comunicação...)
 *   • pdf   — relatório financeiro (reaproveita exportToPDFMulti)
 *   • txt   — snapshots legíveis da configuração (página, técnica, sessões, formulário, display)
 *   • jpg/png — imagens da configuração (logo, hero, hero-mobile, about-image, display custom image)
 */
import JSZip from "jszip";
import * as XLSX from "xlsx";
import axios from "axios";

import { API_URL } from "../config";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers

const ax = { withCredentials: true };

const safeGet = async (path) => {
  try {
    const r = await axios.get(`${API_URL}${path}`, ax);
    return r.data;
  } catch (e) {
    return null;
  }
};

const sheetFromRows = (rows) =>
  Array.isArray(rows) && rows.length > 0 ? XLSX.utils.json_to_sheet(rows) : null;

const addSheetTo = (zip, filename, rows) => {
  const sheet = sheetFromRows(rows);
  if (!sheet) return;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, filename.replace(/\.xlsx$/, "").slice(0, 31));
  const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  zip.file(filename, buf);
};

const txtFromKV = (title, kv) => {
  const lines = [title, "=".repeat(title.length), ""];
  Object.entries(kv).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    lines.push(`${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`);
  });
  return lines.join("\n");
};

const stripHtml = (html) =>
  (html || "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<\/(p|div|h[1-6]|li)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const downloadImage = async (url, fallbackName) => {
  if (!url || typeof url !== "string") return null;
  try {
    // Use the existing image proxy if it's a Google Drive URL (handled server-side)
    const fetchUrl = url.startsWith("http") ? url : `${API_URL}${url}`;
    const res = await fetch(fetchUrl, { credentials: "include" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const ext =
      blob.type.includes("png") ? "png" :
      blob.type.includes("webp") ? "webp" :
      blob.type.includes("gif") ? "gif" :
      "jpg";
    const ab = await blob.arrayBuffer();
    return { name: `${fallbackName}.${ext}`, bytes: ab };
  } catch (e) {
    return null;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Main

export async function archiveEventToZip(onProgress) {
  const progress = (msg) => onProgress && onProgress(msg);

  progress("A obter dados...");
  const [show, sessions, ticketTypes, customFields, coupons, sales, financial, emailHistory] =
    await Promise.all([
      safeGet("/api/show"),
      safeGet("/api/admin/sessions-all"),
      safeGet("/api/admin/ticket-types"),
      safeGet("/api/custom-fields"),
      safeGet("/api/admin/coupons"),
      safeGet("/api/admin/sales"),
      safeGet("/api/admin/financial"),
      safeGet("/api/admin/emails/history"),
    ]);

  const zip = new JSZip();
  const eventName = (show?.name || "Evento").replace(/[^\w\s\-]/g, "").trim() || "Evento";
  const dateStr = new Date().toISOString().slice(0, 10);
  const baseFolder = `${eventName} ${dateStr}`;
  const root = zip.folder(baseFolder);

  // ─── README ────────────────────────────────────────────────────────────
  progress("A gerar README...");
  const paidCount = (sales || []).filter((s) => s.payment_status === "paid").length;
  const totalRevenue = (sales || [])
    .filter((s) => s.payment_status === "paid")
    .reduce((a, s) => a + (s.final_price || 0), 0);
  const readmeLines = [
    `ARQUIVO DE EVENTO — ${eventName}`,
    "=".repeat(40),
    "",
    `Gerado em: ${new Date().toLocaleString("pt-PT")}`,
    `Evento: ${show?.name || "—"}`,
    `Local: ${show?.location || "—"}`,
    `Morada: ${show?.address || "—"}`,
    "",
    `Sessões: ${(sessions || []).length}`,
    `Tipos de bilhete: ${(ticketTypes || []).length}`,
    `Total de vendas (todas): ${(sales || []).length}`,
    `Bilhetes pagos: ${paidCount}`,
    `Receita total: ${totalRevenue.toFixed(2)} €`,
    `Cupões: ${(coupons || []).length}`,
    `Campanhas de email: ${(emailHistory || []).length}`,
    "",
    "CONTEÚDO DO ARQUIVO",
    "-------------------",
    "01-Configuracoes/  → snapshots TXT + imagens da página, técnica, display",
    "02-Sessoes-Bilhetes/  → xlsx das sessões e tipos de bilhete",
    "03-Formulario/  → xlsx com a estrutura do formulário de inscrição",
    "04-Cupoes/  → xlsx com todos os cupões",
    "05-Vendas/  → xlsx vendas + compradores únicos",
    "06-Financeiro/  → xlsx breakdown receita",
    "07-Comunicacao/  → xlsx histórico de campanhas",
    "",
    "Os ficheiros podem ser abertos com Excel, Numbers, ou qualquer leitor de PDF/TXT.",
  ];
  root.file("README.txt", readmeLines.join("\n"));

  // ─── 01 Configurações ──────────────────────────────────────────────────
  progress("A guardar configurações...");
  const conf = root.folder("01-Configuracoes");

  if (show) {
    // Página (textos visíveis)
    conf.file(
      "Pagina.txt",
      txtFromKV("CONFIGURAÇÃO DA PÁGINA PÚBLICA", {
        "Nome do evento": show.name,
        "Descrição": show.description,
        "Local": show.location,
        "Morada": show.address,
        "Preço (referência)": show.price,
        "Badge do hero": show.hero_badge,
        "Texto CTA": show.cta_button_text,
        "Badge 'Sobre'": show.about_badge,
        "Título 'Sobre'": show.about_title,
        "Badge 'Bilhetes'": show.tickets_badge,
        "Título 'Bilhetes'": show.tickets_title,
        "Botão info (texto)": show.about_info_button_text,
        "Botão info (URL)": show.about_info_button_url,
        "About description (texto)": stripHtml(show.about_description_html),
        "Footer texto": show.footer_text,
        "Footer copyright": show.footer_copyright,
        "Footer org": show.footer_org_name,
        "Footer venue": show.footer_venue_name,
        "Hero image URL": show.image_url,
        "Hero mobile URL": show.hero_image_mobile_url,
        "About image URL": show.about_image_url,
        "Logo URL": show.logo_url,
      })
    );

    // Configuração técnica (sem chaves)
    conf.file(
      "Configuracao-Tecnica.txt",
      txtFromKV("CONFIGURAÇÃO TÉCNICA", {
        "Cor da marca (accent)": show.accent_color,
        "Stripe ativo": show.stripe_enabled,
        "Métodos Stripe": (show.stripe_payment_methods || []).join(", "),
        "Email remetente (sender_email)": show.sender_email,
        "WhatsApp admin": show.whatsapp_admin_phone,
        "Resend API key configurada": Boolean(show.resend_api_key) ? "Sim" : "Não",
        "Stripe API key configurada": Boolean(show.stripe_secret_key) ? "Sim" : "Não",
        "CallMeBot API key configurada": Boolean(show.callmebot_api_key) ? "Sim" : "Não",
        "QR code logo URL": show.qr_logo_url,
        "QR code tamanho": show.qr_size,
      })
    );

    // Social
    conf.file(
      "Redes-Sociais.txt",
      txtFromKV("REDES SOCIAIS", {
        "Mostrar links sociais": show.show_social_links,
        "Facebook": show.social_facebook,
        "Instagram": show.social_instagram,
        "Twitter": show.social_twitter,
        "YouTube": show.social_youtube,
        "TikTok": show.social_tiktok,
        "Website": show.social_website,
        "Custom": show.social_custom,
      })
    );

    // Ecrã Check-In
    conf.file(
      "Ecra-Check-In.txt",
      txtFromKV("ECRÃ CHECK-IN (DISPLAY)", {
        "Tema": show.display_theme,
        "Template": show.display_template,
        "Duração (s)": show.display_duration,
        "Som activo": show.display_sound_enabled,
        "Variante VIP": show.display_variant_vip_enabled,
        "Variante aniversário": show.display_variant_birthday_enabled,
        "Variante 1ª vez": show.display_variant_first_time_enabled,
        "Variante grupo": show.display_variant_group_enabled,
        "Mensagem idle": show.display_idle_message,
        "Idle countdown": show.display_idle_show_countdown,
        "Idle image URL": show.display_idle_image_url,
        "Cor personalizada": show.display_custom_color,
        "Imagem custom URL": show.display_custom_image_url,
        "Imagem custom darken": show.display_custom_image_darken,
        "Nome override": show.display_show_name_override,
      })
    );

    // Imagens (jpeg/png/webp)
    progress("A descarregar imagens...");
    const imgPairs = [
      [show.image_url, "hero"],
      [show.hero_image_mobile_url, "hero-mobile"],
      [show.about_image_url, "about"],
      [show.logo_url, "logo"],
      [show.qr_logo_url, "qr-logo"],
      [show.display_idle_image_url, "display-idle"],
      [show.display_custom_image_url, "display-custom"],
    ];
    const downloads = await Promise.all(imgPairs.map(([u, n]) => downloadImage(u, n)));
    downloads.forEach((d) => {
      if (d) conf.file(`imagens/${d.name}`, d.bytes);
    });
  }

  // ─── 02 Sessões e Bilhetes ─────────────────────────────────────────────
  progress("A guardar sessões e bilhetes...");
  const sess = root.folder("02-Sessoes-Bilhetes");
  if (sessions) {
    const rows = sessions.map((s) => ({
      Data: s.date,
      Horário: s.time,
      "Lugares Totais": s.total_seats,
      "Disponíveis": s.available_seats,
      Activa: s.active !== false,
    }));
    addSheetTo(sess, "Sessoes.xlsx", rows);
  }
  if (ticketTypes) {
    const rows = ticketTypes.map((t) => ({
      Nome: t.name,
      Preço: t.price,
      Activo: t.active,
      "Lugares max": t.max_quantity || "—",
      "Descrição": t.description || "",
    }));
    addSheetTo(sess, "Tipos-Bilhete.xlsx", rows);
  }

  // ─── 03 Formulário ─────────────────────────────────────────────────────
  progress("A guardar formulário...");
  const form = root.folder("03-Formulario");
  if (customFields) {
    const rows = customFields.map((f) => ({
      Campo: f.name,
      Etiqueta: f.label,
      Tipo: f.field_type,
      Obrigatório: f.required,
      "Ordem": f.order ?? "",
      "Opções": (f.options || []).join(" | "),
      Placeholder: f.placeholder || "",
    }));
    addSheetTo(form, "Campos-Formulario.xlsx", rows);
  }

  // ─── 04 Cupões ─────────────────────────────────────────────────────────
  progress("A guardar cupões...");
  const coup = root.folder("04-Cupoes");
  if (coupons) {
    const rows = coupons.map((c) => ({
      Código: c.code,
      "Tipo de desconto": c.discount_type,
      Valor: c.discount_value,
      "Usos / Máximo": `${c.uses || 0} / ${c.max_uses === -1 ? "∞" : c.max_uses}`,
      "Email atribuído": c.assigned_email || "",
      Activo: c.active,
      "Válido até": c.valid_until || "",
      "Lote": c.batch_id || "",
    }));
    addSheetTo(coup, "Cupoes.xlsx", rows);
  }

  // ─── 05 Vendas ─────────────────────────────────────────────────────────
  progress("A guardar vendas...");
  const v = root.folder("05-Vendas");
  if (sales) {
    const rows = sales.map((s) => ({
      "Nº Bilhete": s.ticket_number,
      Nome: `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.name,
      Email: s.email,
      Telefone: s.phone,
      Sessão: `${s.session_date || ""} ${s.session_time || ""}`.trim(),
      "Tipo de Bilhete": s.ticket_type_name || "—",
      "Preço base": s.original_price,
      Desconto: s.discount_amount,
      "Valor pago": s.final_price,
      "Estado": s.payment_status,
      "Método": s.payment_method,
      Cupão: s.coupon_code || "",
      Grupo: s.group_code || "",
      "Data Compra": s.created_at,
    }));
    addSheetTo(v, "Vendas.xlsx", rows);

    // Compradores únicos (por email)
    const seen = new Set();
    const unique = [];
    sales.forEach((s) => {
      const email = (s.email || "").toLowerCase();
      if (!email || seen.has(email)) return;
      seen.add(email);
      unique.push({
        Nome: `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.name,
        Email: s.email,
        Telefone: s.phone,
        "Igreja/Grupo": s.church || s.group_code || "",
      });
    });
    addSheetTo(v, "Compradores-unicos.xlsx", unique);

    // Check-ins
    const checkins = sales
      .filter((s) => s.check_in_status === "checked_in" || s.checked_in_at)
      .map((s) => ({
        "Nº Bilhete": s.ticket_number,
        Nome: `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.name,
        Email: s.email,
        Sessão: `${s.session_date || ""} ${s.session_time || ""}`.trim(),
        "Data Check-In": s.checked_in_at || "",
      }));
    if (checkins.length) addSheetTo(v, "Check-Ins.xlsx", checkins);
  }

  // ─── 06 Financeiro ─────────────────────────────────────────────────────
  progress("A guardar financeiro...");
  const fin = root.folder("06-Financeiro");
  if (financial?.session_breakdown) {
    const rows = financial.session_breakdown.map((s) => ({
      Data: s.date,
      Horário: s.time,
      Lugares: s.total_seats,
      Vendidos: s.sold,
      Receita: s.revenue,
    }));
    addSheetTo(fin, "Receita-por-Sessao.xlsx", rows);
  }
  if (sales) {
    const map = new Map();
    sales.forEach((s) => {
      const k = s.ticket_type_name || "(sem tipo)";
      const acc = map.get(k) || { Tipo: k, Total: 0, Pagos: 0, Gratuitos: 0, Pendentes: 0, Receita: 0 };
      acc.Total += 1;
      if (s.payment_status === "paid") {
        const price = Number(s.final_price || 0);
        if (price > 0) { acc.Pagos += 1; acc.Receita += price; } else acc.Gratuitos += 1;
      } else if (s.payment_status === "pending") {
        acc.Pendentes += 1;
      }
      map.set(k, acc);
    });
    addSheetTo(fin, "Receita-por-Tipo.xlsx", Array.from(map.values()));
  }

  // ─── 07 Comunicação ────────────────────────────────────────────────────
  progress("A guardar comunicação...");
  const com = root.folder("07-Comunicacao");
  if (emailHistory) {
    const rows = emailHistory.map((h) => ({
      Assunto: h.subject,
      "Total destinatários": h.total_emails,
      Enviados: h.sent,
      Falhados: h.failed,
      Tema: h.theme || "dark",
      Data: h.sent_at,
    }));
    addSheetTo(com, "Historico-Emails.xlsx", rows);
  }

  // ─── ZIP ───────────────────────────────────────────────────────────────
  progress("A comprimir...");
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${baseFolder}.zip`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  progress("Concluído!");
  return baseFolder;
}
